import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from '../redux/store';
import Layout from '../components/Layout';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  alpha,
  Fade,
  Grow,
} from '@mui/material';
import {
  People as PeopleIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  MoneyOff as MoneyOffIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';

interface DashboardCardProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  link: string;
  color: string;
  index: number;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ title, subtitle, icon, link, color, index }) => {
  return (
    <Grow in={true} timeout={300 + index * 150}>
      <Card
        component={Link}
        to={link}
        sx={{
          textDecoration: 'none',
          height: '100%',
          background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
          border: `1px solid ${alpha(color, 0.2)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-12px) scale(1.02)',
            boxShadow: `0 16px 32px ${alpha(color, 0.25)}`,
            border: `1px solid ${alpha(color, 0.5)}`,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.6)})`,
            transform: 'scaleX(0)',
            transformOrigin: 'left',
            transition: 'transform 0.3s ease',
          },
          '&:hover::before': {
            transform: 'scaleX(1)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <Avatar
              sx={{
                bgcolor: color,
                width: 64,
                height: 64,
                boxShadow: `0 8px 16px ${alpha(color, 0.4)}`,
                transition: 'all 0.3s ease',
              }}
            >
              {icon}
            </Avatar>
          </Box>
          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.875rem',
              lineHeight: 1.6,
            }}
          >
            {subtitle}
          </Typography>
        </CardContent>
      </Card>
    </Grow>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const cards = [
    {
      title: 'Clients',
      subtitle: 'Gérer vos clients et leurs informations',
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      link: '/clients',
      color: '#1976d2',
    },
    {
      title: 'Démarches',
      subtitle: 'Suivre toutes les démarches en cours',
      icon: <DescriptionIcon sx={{ fontSize: 32 }} />,
      link: '/demarches',
      color: '#9c27b0',
    },
    {
      title: 'Dossiers à revoir',
      subtitle: 'Rappels et dossiers nécessitant un suivi',
      icon: <AssignmentIcon sx={{ fontSize: 32 }} />,
      link: '/dossiers-a-revoir',
      color: '#ed6c02',
    },
    {
      title: 'Interventions',
      subtitle: 'Démarches nécessitant une action immédiate',
      icon: <WarningIcon sx={{ fontSize: 32 }} />,
      link: '/interventions-requises',
      color: '#d32f2f',
    },
    {
      title: 'Factures impayées',
      subtitle: 'Gérer les paiements en attente',
      icon: <MoneyOffIcon sx={{ fontSize: 32 }} />,
      link: '/impayees',
      color: '#d32f2f',
    },
    {
      title: 'Factures',
      subtitle: 'Consulter toutes vos factures',
      icon: <ReceiptIcon sx={{ fontSize: 32 }} />,
      link: '/factures',
      color: '#2e7d32',
    },
  ];

  return (
    <Layout>
      <Box>
        {/* Welcome Section */}
        <Fade in={true} timeout={500}>
          <Card
            elevation={3}
            sx={{
              mb: 4,
              background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(255, 255, 255, 1) 50%)',
              borderRadius: 4,
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 3, mb: 3 }}>
                <Avatar
                  sx={{
                    width: { xs: 80, md: 96 },
                    height: { xs: 80, md: 96 },
                    bgcolor: 'primary.main',
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    fontWeight: 700,
                    boxShadow: 4,
                  }}
                >
                  {user?.prenom?.[0]}{user?.nom?.[0]}
                </Avatar>
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 1,
                      fontSize: { xs: '1.75rem', md: '2.5rem' },
                    }}
                  >
                    Bienvenue, {user?.prenom} {user?.nom} !
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                    Votre espace de gestion multi-service
                  </Typography>
                </Box>
              </Box>

              {/* Quick Stats */}
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Grow in={true} timeout={700}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: 1,
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 3,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                        <SpeedIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Activité
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        Active
                      </Typography>
                    </Box>
                  </Grow>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Grow in={true} timeout={900}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: 1,
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 3,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                        <TrendingUpIcon sx={{ color: 'success.main', fontSize: 24 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Performance
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                        Optimale
                      </Typography>
                    </Box>
                  </Grow>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>

        {/* Dashboard Cards */}
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {cards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={card.title}>
              <DashboardCard {...card} index={index} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Layout>
  );
};

export default Dashboard;
